/**
 * GET /crew/me/today — the crew member's day view.
 *
 * Returns the signed-in crew member's tasks for a target date with
 * project info denormalized so the mobile screen renders in one call.
 */
import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { InstallSubtypeSchema, PickupStatusSchema, PickupSubtypeSchema, ProjectStatusSchema, TaskStatusSchema, TaskTypeSchema, } from './enums.js';
import { ScheduleDaySchema } from './projects.js';
import { CrewMemberWithUserSchema } from './users.js';
/** A teammate assigned to the same task — for the "who you're with" avatars. */
export const CrewTodayCrewSchema = z.object({
    display_name: z.string(),
    crew_role: z.string(),
    avatar_url: z.string().url().nullable(),
    is_lead: z.boolean(),
});
export const CrewTodayTaskSchema = z.object({
    id: UuidSchema,
    project_id: UuidSchema,
    project_title: z.string(),
    project_client_name: z.string(),
    // Site address (just line1 + city is enough for the mobile card)
    site_short_address: z.string().nullable(),
    type: TaskTypeSchema,
    pickup_subtype: PickupSubtypeSchema.nullable(),
    install_subtype: InstallSubtypeSchema.nullable(),
    sap_rma_reference: z.string().nullable(),
    title: z.string(),
    description: z.string().nullable(),
    status: TaskStatusSchema,
    scheduled_start: IsoDateTimeSchema.nullable(),
    scheduled_end: IsoDateTimeSchema.nullable(),
    started_at: IsoDateTimeSchema.nullable(),
    completed_at: IsoDateTimeSchema.nullable(),
    estimated_duration_minutes: z.number().int().min(0).nullable(),
    is_lead: z.boolean(),
    /** Everyone assigned to this task (lead first) — the crew you're working with. */
    crew: z.array(CrewTodayCrewSchema).default([]),
    /** The project's approved working window for this date (per-day schedule);
     *  times are business-local HH:MM. Null when no per-day schedule applies. */
    project_day_window: ScheduleDaySchema.nullable().optional(),
});
/**
 * A PROJECT the crew member is assigned to directly (D-3/A3) — one entry per
 * project-anchored assignment block on the day. Sits beside `tasks`: the crew
 * Start/Finish the project itself; its tasks (if any) are an optional checklist.
 */
export const CrewTodayProjectSchema = z.object({
    /** The assignment block this entry comes from (one per crew member per day). */
    block_id: UuidSchema,
    project_id: UuidSchema,
    project_title: z.string(),
    project_client_name: z.string(),
    site_short_address: z.string().nullable(),
    status: ProjectStatusSchema,
    /** The block's committed window for this day. */
    scheduled_start: IsoDateTimeSchema.nullable(),
    scheduled_end: IsoDateTimeSchema.nullable(),
    /** Direct project work stamps: first Start → on_site; Finish → pending_closeout. */
    started_at: IsoDateTimeSchema.nullable(),
    finished_at: IsoDateTimeSchema.nullable(),
    is_lead: z.boolean(),
    /** Everyone on the project that day (project blocks) — "who you're with". */
    crew: z.array(CrewTodayCrewSchema).default([]),
    project_day_window: ScheduleDaySchema.nullable().optional(),
});
/**
 * A PICKUP the crew member is assigned to (workstream C) — a warehouse run
 * spanning N projects. Sits beside `tasks` / `projects`: the crew tap it to open
 * the pickup screen (route + packing lists to check off). One entry per
 * pickup-anchored block (one per crew per pickup).
 */
export const CrewTodayPickupSchema = z.object({
    block_id: UuidSchema,
    pickup_id: UuidSchema,
    /** The pickup's display title (its own, or derived from the stops). */
    title: z.string(),
    status: PickupStatusSchema,
    scheduled_start: IsoDateTimeSchema.nullable(),
    scheduled_end: IsoDateTimeSchema.nullable(),
    started_at: IsoDateTimeSchema.nullable(),
    finished_at: IsoDateTimeSchema.nullable(),
    is_lead: z.boolean(),
    /** Everyone on the pickup — "who you're with". */
    crew: z.array(CrewTodayCrewSchema).default([]),
    /** How many projects' orders this one trip services. */
    project_count: z.number().int(),
});
export const CrewTodayResponseSchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    crew_member: CrewMemberWithUserSchema.nullable().describe('null when the signed-in user is not a crew member'),
    tasks: z.array(CrewTodayTaskSchema),
    /** Projects the crew member works directly today (D-3/A3). */
    projects: z.array(CrewTodayProjectSchema).default([]),
    /** Pickups the crew member is on today (workstream C). */
    pickups: z.array(CrewTodayPickupSchema).default([]),
    active_task_id: UuidSchema.nullable(),
    next_task_id: UuidSchema.nullable(),
    crewed_minutes_today: z.number().min(0),
});
export const CrewTodayQuerySchema = z.object({
    date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD')
        .optional()
        .describe('Defaults to today in the server timezone'),
    crew_member_id: z
        .string()
        .uuid()
        .optional()
        .describe('View another crew member (supervisors/admins only); defaults to self'),
});
// ----------------------------------------------------------------------------
// GET /crew/me/schedule — the crew member's multi-day agenda ("My Week").
// ----------------------------------------------------------------------------
export const CrewScheduleItemSchema = CrewTodayTaskSchema.extend({
    truck_label: z.string().nullable(),
});
export const CrewScheduleDaySchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    items: z.array(CrewScheduleItemSchema),
    /** Projects worked directly that day (D-3/A3), beside the task items. */
    projects: z.array(CrewTodayProjectSchema).default([]),
    /** Pickups that day (workstream C), beside the task items + projects. */
    pickups: z.array(CrewTodayPickupSchema).default([]),
});
export const CrewScheduleResponseSchema = z.object({
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    days: z.array(CrewScheduleDaySchema),
});
export const CrewScheduleQuerySchema = z.object({
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD').optional(),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD').optional(),
    crew_member_id: z
        .string()
        .uuid()
        .optional()
        .describe('View another crew member (supervisors/admins only); defaults to self'),
});
//# sourceMappingURL=crew-today.js.map